import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { IContactServiceAdapter } from '../types/adapter.types'
import type { ContactTranslations } from '../i18n/translations.types'
import type { PartialTranslations } from '../i18n/mergeTranslations'
import { defaultTranslations } from '../i18n/defaultTranslations'
import { mergeTranslations } from '../i18n/mergeTranslations'

const ContactServiceContext = createContext<IContactServiceAdapter | null>(null)
const TranslationsContext = createContext<ContactTranslations>(defaultTranslations)

export function useContactServiceAdapter(): IContactServiceAdapter {
  const adapter = useContext(ContactServiceContext)
  if (!adapter) {
    throw new Error('useContactServiceAdapter must be used within a ContactServiceProvider')
  }
  return adapter
}

export function useTranslations(): ContactTranslations {
  return useContext(TranslationsContext)
}

interface ContactServiceProviderProps {
  adapter: IContactServiceAdapter
  translations?: PartialTranslations
  children: ReactNode
}

export function ContactServiceProvider({ adapter, translations, children }: ContactServiceProviderProps) {
  const merged = useMemo(
    () => mergeTranslations(defaultTranslations, translations),
    [translations],
  )

  return (
    <ContactServiceContext.Provider value={adapter}>
      <TranslationsContext.Provider value={merged}>
        {children}
      </TranslationsContext.Provider>
    </ContactServiceContext.Provider>
  )
}
